import React from 'react';
import { useChatStore } from '../../store/chatStore';
import { SuggestedQuestion } from '../../types/chat';

/**
 * SuggestedQuestions - Displays clickable question chips grouped by category
 *
 * Requirements:
 * - 5.1: Display minimum 6 suggested questions as clickable chips when conversation is empty
 * - 5.2: On chip click, populate input and submit automatically
 * - 5.3: Cover each supported intent category with at least one example
 * - 5.4: Group questions by category with labels
 */

const CATEGORY_LABELS: Record<SuggestedQuestion['category'], string> = {
  cost_overview: 'Cost Overview',
  anomalies: 'Anomalies',
  optimization: 'Optimization',
  forecasts: 'Forecasts',
  accounts: 'Accounts',
  setup: 'Setup',
};

const CATEGORY_ORDER: SuggestedQuestion['category'][] = [
  'cost_overview',
  'anomalies',
  'optimization',
  'forecasts',
  'accounts',
  'setup',
];

export const SuggestedQuestions: React.FC = () => {
  const { suggestedQuestions, sendMessage } = useChatStore();

  if (suggestedQuestions.length === 0) {
    return null;
  }

  // Group questions by category
  const grouped = CATEGORY_ORDER.reduce(
    (acc, category) => {
      const questions = suggestedQuestions.filter((q) => q.category === category);
      if (questions.length > 0) {
        acc.push({ category, label: CATEGORY_LABELS[category], questions });
      }
      return acc;
    },
    [] as { category: string; label: string; questions: SuggestedQuestion[] }[]
  );

  const handleChipClick = (question: SuggestedQuestion) => {
    sendMessage(question.text);
  };

  return (
    <div
      className="space-y-4"
      aria-label="Suggested questions"
      role="region"
    >
      {grouped.map(({ category, label, questions }) => (
        <div key={category}>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </h3>
          <div className="flex flex-wrap gap-2">
            {questions.map((question) => (
              <button
                key={question.id}
                onClick={() => handleChipClick(question)}
                aria-label={`Ask: ${question.text}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground/80 transition-colors hover:border-primary/30 hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring dark:border-border dark:bg-secondary dark:text-foreground dark:hover:border-primary/50 dark:hover:bg-secondary dark:hover:text-primary"
              >
                {question.text}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
